/** How a panel reports failure: whole-panel messages, and columns that failed while the rest loaded. */

import { extractErrorMessage } from '@/utils/errorHandler'

/** Shown when a panel fails without a message worth putting in front of the user. */
export const PANEL_ERROR_FALLBACK = 'Failed to load panel data'

/**
 * Message for one failed `/v3/fetch-data` item.
 *
 * Whatever the backend said is shown as-is; the status line is only for an item that carried no
 * message at all. Reads the whole response body rather than just `error_message`, because the panel
 * endpoints also reject with `{ span, message }` — an aggregate expression that won't parse, say —
 * and reading the one key dropped that text while the same request sent individually surfaced it.
 */
export function resolveBatchItemError(item: { status: number; response: unknown }): string {
	return extractErrorMessage(item.response, `Request failed with status ${item.status}`)
}

/**
 * Why a batched item failed, or undefined if it succeeded: no item at all, a non-2xx status, an
 * empty body, or a 2xx carrying a column error in `errors`.
 *
 * Stricter than panelDataService's isItemSuccessful on one point — a 2xx with an empty body counts
 * as a failure here, because callers go on to read into that body. Over-reporting it is harmless:
 * the resolvers only run once something else has already failed.
 */
export function batchItemFailure(item: { status: number; response: unknown } | undefined): string | undefined {
	if (!item) {
		return PANEL_ERROR_FALLBACK
	}
	if (item.status < 200 || item.status >= 300) {
		return resolveBatchItemError(item)
	}
	// The status says success, so quoting it back ("Request failed with status 200") would read as a
	// contradiction — there is no backend message to show either, hence the generic fallback.
	if (!item.response) {
		return PANEL_ERROR_FALLBACK
	}
	return extractEmbeddedResponseError(item.response)
}

/** Marker for a fetch cancelled by navigation/refresh — internal plumbing, never user-facing. */
export const PANEL_REQUEST_ABORTED = 'Request aborted'

/**
 * Message to render for a failed panel: the backend's own text when it says something ('invalid device
 * filters'), otherwise the fallback.
 */
export const resolvePanelErrorMessage = (message?: string): string => {
	const trimmed = message?.trim()
	if (!trimmed || trimmed === PANEL_REQUEST_ABORTED) {
		return PANEL_ERROR_FALLBACK
	}
	return trimmed
}

/**
 * A configured column that failed to load (e.g. it no longer exists on the stream).
 *
 * `index` is the column's fixed position in the panel's configured `columns` array (not its
 * position within this failedColumns list, which shrinks/grows/reorders as the failing set
 * changes across refreshes). Pair it with `name` for a React key that's both guaranteed-unique
 * (protects against duplicate column names — reachable by hand-editing a dashboard's raw JSON
 * config) and stable across refreshes.
 */
export interface FailedColumn {
	name: string
	error: string
	index: number
	/** Not a stream column — a panel extra like a histogram or row count. Only changes the wording. */
	isExtra?: boolean
}

/** Heading for a list of failures: "column(s)" only when every entry really is one. */
export function failedColumnsLabel(failed: readonly FailedColumn[]): string {
	if (failed.some(item => item.isExtra)) {
		return failed.length === 1 ? 'Failed to load:' : `${failed.length} failed to load:`
	}
	return failed.length === 1 ? '1 column failed to load:' : `${failed.length} columns failed to load:`
}

/**
 * A `/v3/timeseries-sampled` response (single-request or batched) can carry a column/query-level
 * failure inside an otherwise HTTP-200 body, as an `errors` array — e.g. ClickHouse's "invalid
 * column" for a stale/renamed/removed column. This is distinct from a request-level failure (a
 * thrown error, or a non-2xx `status` on a batch item), which callers already handle separately.
 * Without this check such a column silently renders as "no data" instead of surfacing as failed.
 *
 * Accepts either shape a caller might have on hand: the batched item's unwrapped single chart
 * result (`{ data, errors }`), or the individual endpoint's array-wrapped response (`[{ data }]`).
 */
export function extractEmbeddedResponseError(response: unknown): string | undefined {
	const target = Array.isArray(response) ? response[0] : response
	if (!target || typeof target !== 'object') {
		return undefined
	}
	const errors = (target as { errors?: unknown }).errors
	if (!Array.isArray(errors) || errors.length === 0) {
		return undefined
	}
	const first = errors[0]
	if (typeof first === 'string') {
		return first
	}
	if (first && typeof first === 'object' && typeof (first as { message?: unknown }).message === 'string') {
		return (first as { message: string }).message
	}
	return 'Failed to load column'
}

/**
 * Throws when a 200 response carries an embedded `errors` array.
 *
 * For panels with no per-column error state — without this they'd render a silently short chart
 * where they used to show an error. Last Value / LED map those errors to `failedColumns` instead.
 */
export function assertNoEmbeddedResponseError(response: unknown): void {
	const message = extractEmbeddedResponseError(response)
	if (message !== undefined) {
		throw new Error(message)
	}
}

/**
 * Attaches failures without changing the data's shape. Array-shaped panel data (Line Chart, the map
 * panels) must keep being an array — `{ ...data, failedColumns }` would turn it into an object and
 * silently stop `noData` firing, since that check only recognises arrays.
 */
export function attachFailedColumns<T>(data: T, failedColumns: FailedColumn[]): T {
	if (failedColumns.length === 0) {
		return data
	}
	return Array.isArray(data) ? (Object.assign(data, { failedColumns }) as T) : ({ ...data, failedColumns } as T)
}

/** Reads `failedColumns` off panel data returned by panel types that support partial column failure. */
export function getFailedColumns(data: unknown): FailedColumn[] | undefined {
	if (!data || typeof data !== 'object') {
		return undefined
	}
	const candidate = (data as { failedColumns?: unknown }).failedColumns
	return Array.isArray(candidate) ? (candidate as FailedColumn[]) : undefined
}

/** Fields object-shaped panel data carries its rows under. */
export const PANEL_ROW_FIELDS = ['data', 'all-data', 'allData'] as const

const hasRows = (data: unknown): boolean => {
	if (Array.isArray(data)) {
		return data.length > 0
	}
	if (!data || typeof data !== 'object') {
		return data !== undefined && data !== null
	}
	const payload = data as Record<string, unknown>
	return PANEL_ROW_FIELDS.some(field => {
		const rows = payload[field]
		if (Array.isArray(rows)) {
			return rows.length > 0
		}
		// Aggregate Value holds its values in an object (`{ value }`, or one key per aggregator) rather
		// than a row array; a surviving value there still counts as something to draw.
		return (
			!!rows && typeof rows === 'object' && Object.values(rows).some(value => value !== null && value !== undefined)
		)
	})
}

/**
 * Column failures that left the panel with nothing to draw, as a message for the panel body.
 *
 * A panel that loses some columns but still has rows keeps rendering them and reports the failure through
 * the header info icon. When no rows survive, that icon is the only sign anything went wrong and the body
 * is a bare grid — so callers promote this to the panel's error state, where a whole-panel failure already
 * reports itself. Returns undefined when rows survived, when nothing failed, or when only extras failed.
 */
export function getBlockingColumnFailure(data: unknown): string | undefined {
	const failed = getFailedColumns(data)
	if (!failed?.length || hasRows(data)) {
		return undefined
	}
	// Extras aren't the panel's content: the rows request succeeded and simply returned nothing, so
	// the body keeps its empty state and the header reports the failure.
	if (failed.every(item => item.isExtra)) {
		return undefined
	}
	if (failed.length === 1) {
		return `${failed[0].name}: ${failed[0].error}`
	}
	const names = failed.map(column => column.name).join(', ')
	return `${failedColumnsLabel(failed)} ${names}`
}
